import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import LangSelector from "./LangSelector";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const items = [
    { name: t("home.title"), link: "/" },
    { name: t("services.title"), link: "/services" },
    { name: t("home.about-us"), link: "/about" },
    { name: t("contact.title"), link: "/contact" },
  ];

  const demoUrl =
    i18n.language === "ar"
      ? "https://2d3efo5hueq.typeform.com/to/kTNqfk6m?utm_source=website"
      : "https://2d3efo5hueq.typeform.com/to/L2DLllNq?utm_source=website";

  const closeMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" onClick={closeMenu}>
          <Logo variant="dark" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-medium">
          {items.map((item) => (
            <NavLink
              key={item.link}
              to={item.link}
              end={item.link === "/"}
              className={({ isActive }) =>
                `transition-colors hover:text-brand ${
                  isActive ? "text-brand font-extrabold" : "text-brand/80"
                }`
              }
            >
              {item.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangSelector />
          <a
            href={demoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-brand text-white px-6 py-2.5 rounded-full font-bold hover:bg-brand-hover transition-all shadow-md text-sm"
          >
            {t("home.get-started")}
            <ArrowUpRight className="w-4 h-4 rtl:-scale-x-100" />
          </a>
        </div>

        <button
          className="md:hidden p-2 text-brand"
          onClick={() => setIsMobileMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white border-b border-gray-100 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4 font-medium">
              {items.map((item) => (
                <NavLink
                  key={item.link}
                  to={item.link}
                  end={item.link === "/"}
                  className={({ isActive }) =>
                    `py-2 border-b border-gray-50 transition-colors ${
                      isActive ? "text-brand font-extrabold" : "text-brand/80"
                    }`
                  }
                  onClick={closeMenu}
                >
                  {item.name}
                </NavLink>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
                <LangSelector variant="inline" />
                <a
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 py-3 px-6 bg-brand text-white rounded-full font-bold text-sm"
                  onClick={closeMenu}
                >
                  {t("home.get-started")}
                  <ArrowUpRight className="w-4 h-4 rtl:-scale-x-100" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
